<script lang="ts">
  /** Mini-diorama interior for one room (Fallout-Shelter style). Pure SVG in a
   *  120×60 box, cropped to fill the room card (xMidYMax slice keeps the floor
   *  line anchored). Same hand-drawn style family as ColonistSprite. */
  import type { RoomType } from '@sim/types';
  import type { Zone } from '@sim/config/zones';

  export let type: RoomType;
  export let staffed = false;
  export let night = false;
  export let tier = 1; // upgrades add visible props
  export let zone: Zone = 'upper'; // basement = pipes/cables, penthouse = view windows
  export let stock = 0; // building product on hand (stash duffels, deal bricks)
  export let meals = 0; // larder (diner plates)
  export let rain = false; // streaks on exterior windows

  $: duffels = Math.min(4, Math.floor(stock / 15));
  $: bricks = Math.min(4, Math.ceil(stock / 12));
  $: plates = Math.min(3, Math.floor(meals / 25));
</script>

<svg class="scene" class:night class:staffed viewBox="0 0 120 60" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
  <!-- back wall + floor, shared by every room -->
  <rect x="0" y="0" width="120" height="60" fill="#12121b" />
  <rect x="0" y="0" width="120" height="8" fill="#0c0c14" />
  <rect x="0" y="52" width="120" height="8" fill="#0a0a10" />
  <rect x="0" y="51" width="120" height="1.4" fill="#1e1e2c" />

  {#if zone === 'penthouse'}
    <!-- floor-to-ceiling view behind the furniture + gold trim -->
    <rect x="6" y="10" width="108" height="26" rx="1" fill={night ? '#0a0f1f' : '#3d5480'} />
    {#if night}
      {#each [14, 27, 42, 58, 71, 87, 99, 108] as wx, i}
        <rect x={wx} y={18 + ((i * 7) % 12)} width="1.6" height="1.6" fill="#ffd88c" opacity="0.8" />
      {/each}
    {:else}
      <rect x="6" y="28" width="108" height="8" fill="#8a5a40" opacity="0.5" />
    {/if}
    {#each [30, 66, 96] as mx}
      <rect x={mx} y="10" width="1.4" height="26" fill="#12121b" />
    {/each}
    <rect x="4" y="8" width="112" height="1.6" fill="#b89a5a" opacity="0.8" />
  {:else if zone === 'basement'}
    <!-- exposed ceiling pipe, hanging cable, damp stains -->
    <rect x="0" y="9.5" width="120" height="2.2" fill="#343945" />
    {#each [18, 62, 104] as jx}
      <rect x={jx} y="8.8" width="3" height="3.6" rx="1" fill="#454b59" />
    {/each}
    <path d="M34 11.5 q6 9 14 0" stroke="#20202c" stroke-width="1" fill="none" />
    <rect x="4" y="14" width="5" height="20" fill="#0e0e16" opacity="0.7" />
    <rect x="110" y="18" width="6" height="26" fill="#0e0e16" opacity="0.6" />
  {/if}

  {#if type === 'grow'}
    <!-- hydroponic trays under grow-lights -->
    {#each [{ y: 24 }, { y: 42 }] as row}
      <rect x="8" y={row.y - 13} width="104" height="2.5" rx="1" fill="#241f33" />
      <rect x="10" y={row.y - 10.5} width="100" height="2" rx="1" class="growlamp" fill="#c95cff" />
      <rect x="8" y={row.y - 9} width="104" height="9" fill="#c95cff" opacity={night ? 0.16 : 0.09} />
      <rect x="8" y={row.y + 3.5} width="104" height="3" rx="1" fill="#2a2438" />
      {#each [16, 34, 52, 70, 88, 103] as px}
        <rect x={px - 3.5} y={row.y - 1.5} width="7" height="5.5" rx="1" fill="#3a3145" />
        <ellipse cx={px - 2} cy={row.y - 3.5} rx="3.4" ry="2.6" fill="#2f8a44" />
        <ellipse cx={px + 2} cy={row.y - 3} rx="3" ry="2.3" fill="#3fae54" />
        <ellipse cx={px} cy={row.y - 5} rx="2.6" ry="2.2" fill="#57c76c" />
      {/each}
    {/each}
    {#if tier >= 2}
      <rect x="30" y="30" width="60" height="1.4" rx="0.7" class="growlamp" fill="#e18cff" />
    {/if}
    {#if tier >= 3}
      <rect x="108" y="34" width="8" height="18" rx="3" fill="#3a4048" stroke="#20242a" stroke-width="0.8" />
      <rect x="110.5" y="31" width="3" height="4" fill="#586068" />
      <circle cx="112" cy="30" r="1.4" fill="#2ed3c6" />
    {/if}
    {#if staffed}
      <ellipse class="haze" cx="60" cy="30" rx="34" ry="12" fill="#7fdc92" />
    {/if}

  {:else if type === 'deal'}
    <!-- street counter, register, neon OPEN in the window (kept inside the
         slice-crop safe area so the sign never clips at the room edge) -->
    <rect x="68" y="12" width="34" height="32" rx="2" fill="#0a0d16" stroke="#2a2a3c" stroke-width="1.5" />
    <line x1="85" y1="12" x2="85" y2="44" stroke="#2a2a3c" stroke-width="1" />
    <circle cx="75" cy="18" r="0.9" fill="#ffd54a" opacity="0.8" />
    <circle cx="95" cy="22" r="0.8" fill="#8ecbff" opacity="0.7" />
    <circle cx="90" cy="16" r="0.7" fill="#ffd54a" opacity="0.5" />
    <text x="85" y="33" text-anchor="middle" class="neon" font-size="7.5" fill="#ff5470">OPEN</text>
    <rect x="10" y="32" width="52" height="4" rx="1.5" fill="#3b3040" />
    <rect x="13" y="36" width="46" height="17" fill="#2b2333" />
    <rect x="13" y="36" width="46" height="2.5" fill="#00000055" />
    <rect x="20" y="24" width="12" height="8" rx="1" fill="#23232f" />
    <rect x="21.5" y="26" width="9" height="3" fill="#8affc1" opacity="0.9" />
    <rect x="40" y="27.5" width="9" height="4.5" rx="0.8" fill="#2f5c37" />
    <rect x="41" y="26" width="7" height="1.8" rx="0.8" fill="#3fae54" />
    <!-- product bricks on the counter track the stash -->
    {#each Array(bricks) as _, i}
      <rect x={50 + (i % 2) * 6} y={29.5 - Math.floor(i / 2) * 3} width="5.4" height="2.6" rx="0.6" fill="#e8e3d4" stroke="#b9b2a0" stroke-width="0.5" />
    {/each}
    {#if tier >= 2}
      <rect x="30" y="28" width="7" height="4" rx="0.8" fill="#2f5c37" />
      <rect x="31" y="26.6" width="5" height="1.6" rx="0.8" fill="#3fae54" />
    {/if}
    {#if tier >= 3}
      <rect x="10" y="30.6" width="52" height="1.4" fill="#b89a5a" opacity="0.9" />
      <rect x="72" y="38" width="26" height="1.6" rx="0.8" class="neonbar" fill="#ff5470" />
    {/if}
    {#if staffed}
      <text class="dollar d1" x="30" y="26" font-size="7" fill="#7ce98f">$</text>
      <text class="dollar d2" x="48" y="27" font-size="6" fill="#7ce98f">$</text>
    {/if}

  {:else if type === 'lab'}
    <!-- cut lab: bench, glassware, wall shelf -->
    <rect x="72" y="16" width="40" height="2.2" rx="1" fill="#2a2438" />
    {#each [{ x: 78, h: 6, c: '#7f5cff' }, { x: 88, h: 8, c: '#9d6bff' }, { x: 99, h: 5, c: '#5c8aff' }] as b}
      <rect x={b.x} y={16 - b.h - 1} width="5" height={b.h} rx="1" fill={b.c} opacity="0.75" />
    {/each}
    <rect x="8" y="38" width="62" height="3.5" rx="1.5" fill="#332b44" />
    <rect x="11" y="41.5" width="56" height="12" fill="#241f31" />
    <path d="M22 38 l-4.5 -0 l-3 -10 l4 0 z" fill="#cfd8ff" opacity="0.35" transform="translate(4 0)" />
    <path d="M20 28 h5 l3 10 h-11 z" fill="#1b1626" stroke="#7a6f9a" stroke-width="0.8" />
    <path d="M17.6 34.5 h9.8 l0.9 3.5 h-11.6 z" fill="#9d6bff" opacity="0.85" />
    <circle cx="41" cy="32" r="5.4" fill="#1b1626" stroke="#7a6f9a" stroke-width="0.8" />
    <path d="M35.9 33.5 a5.4 5.4 0 0 0 10.2 0 z" fill="#c95cff" opacity="0.85" />
    <rect x="40" y="24" width="2" height="4" fill="#7a6f9a" />
    <rect x="52" y="29" width="12" height="9" rx="1" fill="#23232f" />
    {#each [54.5, 58, 61.5] as tx}
      <rect x={tx} y="26" width="2.2" height="9" rx="1" fill="#8de0ff" opacity="0.7" />
    {/each}
    {#if tier >= 2}
      <path d="M32 38 h4.5 l2.6 8.5 h-9.7 z" fill="#1b1626" stroke="#7a6f9a" stroke-width="0.7" transform="translate(0 -8.5)" />
      <path d="M30.5 34.5 h7.5 l0.8 3 h-9.1 z" fill="#5c8aff" opacity="0.8" transform="translate(0 -4.5)" />
    {/if}
    {#if tier >= 3}
      {#each [70, 76, 82] as coilx}
        <circle cx={coilx} cy="24" r="2.6" fill="none" stroke="#9d6bff" stroke-width="0.9" opacity="0.8" />
      {/each}
      <line x1="64" y1="24" x2="86" y2="24" stroke="#7a6f9a" stroke-width="0.7" />
    {/if}
    {#if staffed}
      <circle class="bubble b1" cx="41" cy="34" r="1" fill="#e7c9ff" />
      <circle class="bubble b2" cx="39.5" cy="35" r="0.7" fill="#e7c9ff" />
    {/if}

  {:else if type === 'pack'}
    <!-- packaging line: conveyor, boxes, sealed bricks -->
    <rect x="6" y="41" width="80" height="4.5" rx="2" fill="#2e2836" />
    {#each [14, 30, 46, 62, 78] as cx}
      <circle cx={cx} cy="47.5" r="2.2" fill="#1c1922" stroke="#3a3345" stroke-width="0.8" />
    {/each}
    <rect x="10" y="48" width="4" height="6" fill="#23202b" />
    <rect x="76" y="48" width="4" height="6" fill="#23202b" />
    {#each [{ x: 18 }, { x: 42 }, { x: 64 }] as bx}
      <rect x={bx.x} y="32" width="12" height="9" rx="1" fill="#b8834a" />
      <rect x={bx.x} y="35.5" width="12" height="1.8" fill="#e8d9a0" />
      <rect x={bx.x + 4.8} y="32" width="2.4" height="9" fill="#00000022" />
    {/each}
    {#each [0, 1, 2] as row}
      {#each [0, 1] as col}
        <rect x={92 + col * 11} y={45 - row * 7.5} width="10" height="6.5" rx="1" fill="#caa25e" stroke="#8a6a35" stroke-width="0.7" />
      {/each}
    {/each}
    <line x1="28" y1="8" x2="28" y2="16" stroke="#4a4456" stroke-width="1" />
    <path d="M23 16 h10 l-1.6 5 h-6.8 z" fill="#3a3345" />
    {#if tier >= 2}
      <rect x="30" y="24" width="10" height="7.5" rx="1" fill="#a5763f" />
      <rect x="30" y="26.8" width="10" height="1.6" fill="#e8d9a0" />
    {/if}
    {#if tier >= 3}
      {#each [0, 1] as col}
        <rect x={92 + col * 11} y="15" width="10" height="6.5" rx="1" fill="#caa25e" stroke="#8a6a35" stroke-width="0.7" />
      {/each}
      <circle cx="70" cy="12" r="1.6" fill="#ff5470" opacity="0.9" />
    {/if}
    {#if staffed}<rect class="beltglow" x="6" y="41" width="80" height="1" fill="#ffb454" />{/if}

  {:else if type === 'bunk'}
    <!-- two bunk beds -->
    {#each [{ x: 12 }, { x: 66 }] as bed}
      <rect x={bed.x} y="20" width="2.4" height="34" fill="#3d3140" />
      <rect x={bed.x + 39.6} y="20" width="2.4" height="34" fill="#3d3140" />
      {#each [{ y: 30 }, { y: 46 }] as lvl}
        <rect x={bed.x} y={lvl.y} width="42" height="2.4" fill="#3d3140" />
        <rect x={bed.x + 2} y={lvl.y - 4.5} width="38" height="4.5" rx="1.5" fill="#31415e" />
        <rect x={bed.x + 3.5} y={lvl.y - 6} width="9" height="3.5" rx="1.5" fill="#c7cede" />
      {/each}
    {/each}
    <rect x="53" y="14" width="10" height="13" rx="0.8" fill="#232433" stroke="#3c3d52" stroke-width="0.7" />
    <circle cx="58" cy="19" r="2.6" fill="#ff5470" opacity="0.75" />
    <rect x="55" y="23" width="6" height="0.9" fill="#5b5d7a" />
    <circle class="lamp" cx="34" cy="12" r="2" fill="#ffd9a0" />
    <line x1="34" y1="8" x2="34" y2="10" stroke="#4a4456" stroke-width="0.8" />
    {#if tier >= 2}
      <ellipse cx="58" cy="52" rx="14" ry="2.4" fill="#4a2f4e" />
      <rect x="30" y="14" width="9" height="11" rx="0.8" fill="#1d2a3c" stroke="#3c3d52" stroke-width="0.7" />
      <path d="M31.5 22 l3 -4 l2.4 2.6 l2 -3" stroke="#4ea8ff" stroke-width="0.8" fill="none" />
    {/if}
    {#if tier >= 3}
      <circle class="lamp" cx="16" cy="24" r="1.5" fill="#ffd9a0" />
      <circle class="lamp" cx="104" cy="24" r="1.5" fill="#ffd9a0" />
    {/if}

  {:else if type === 'diner'}
    <!-- counter, stools, menu board, pendant lamps -->
    <rect x="42" y="10" width="36" height="11" rx="1" fill="#1d2430" stroke="#39435a" stroke-width="0.8" />
    {#each [13.5, 16.5, 19] as ly}
      <rect x="45" y={ly} width={ly === 16.5 ? 22 : 28} height="1.2" fill="#7f8aa8" opacity="0.7" />
    {/each}
    {#each [{ x: 20 }, { x: 96 }] as pl}
      <line x1={pl.x} y1="8" x2={pl.x} y2="15" stroke="#4a4456" stroke-width="0.9" />
      <path d="M{pl.x - 4} 19 a4 4 0 0 1 8 0 z" fill="#3a3345" transform="rotate(180 {pl.x} 17)" />
      <circle class="lamp" cx={pl.x} cy="17.5" r="1.6" fill="#ffd9a0" />
    {/each}
    <rect x="8" y="34" width="58" height="4" rx="1.5" fill="#8a3f47" />
    <rect x="11" y="38" width="52" height="15" fill="#5d2c33" />
    <rect x="11" y="38" width="52" height="2" fill="#00000044" />
    <rect x="18" y="25" width="10" height="9" rx="1" fill="#2b2b38" />
    <rect x="20" y="27" width="3.5" height="4" fill="#151520" />
    <circle cx="26" cy="28.5" r="0.8" fill="#ff5470" />
    <!-- plated meals track the larder -->
    {#each Array(1 + plates) as _, i}
      <ellipse cx={44 + i * 8} cy="33" rx="4.6" ry="1.5" fill="#d9dde8" />
      <ellipse cx={44 + i * 8} cy="32.4" rx="2.7" ry="0.9" fill={i % 2 ? '#c96a4a' : '#e8b04a'} />
    {/each}
    {#if tier >= 2}
      <rect x="33" y="27" width="7" height="6" rx="0.8" fill="#2b2b38" />
      <path d="M33.8 27 a2.8 2.8 0 0 1 5.4 0 z" fill="#cfd4e2" opacity="0.8" />
    {/if}
    {#if tier >= 3}
      <rect x="44" y="21.5" width="24" height="1.3" rx="0.6" class="neonbar" fill="#ffd54a" />
    {/if}
    {#each [{ x: 78 }, { x: 96 }] as st}
      <rect x={st.x - 5} y="38" width="10" height="3" rx="1.5" fill="#a4485a" />
      <rect x={st.x - 1} y="41" width="2" height="11" fill="#2c2c3a" />
      <rect x={st.x - 4} y="51" width="8" height="1.6" rx="0.8" fill="#2c2c3a" />
    {/each}
    {#if staffed}
      <path class="steam s1" d="M42 30 q1.5 -3 0 -6 q-1.5 -3 0 -5" stroke="#cfd4e2" stroke-width="1" fill="none" />
      <path class="steam s2" d="M47 30 q-1.5 -3 0 -6 q1.5 -3 0 -5" stroke="#cfd4e2" stroke-width="0.8" fill="none" />
    {/if}

  {:else if type === 'rec'}
    <!-- couch + TV + arcade cabinet -->
    <ellipse cx="44" cy="52" rx="30" ry="3" fill="#20222e" />
    <rect x="10" y="30" width="34" height="10" rx="2" fill="#7c3aa8" />
    <rect x="10" y="38" width="34" height="9" rx="2" fill="#8f46bf" />
    <rect x="7" y="34" width="5" height="13" rx="2" fill="#7c3aa8" />
    <rect x="42" y="34" width="5" height="13" rx="2" fill="#7c3aa8" />
    <rect x="60" y="42" width="30" height="3" rx="1" fill="#2c2c3a" />
    <rect x="72" y="45" width="6" height="8" fill="#23232f" />
    <rect x="61" y="24" width="28" height="18" rx="1.5" fill="#191925" stroke="#3a3a4c" stroke-width="1" />
    <rect class="tv" x="63" y="26" width="24" height="14" fill="#3f6fae" />
    <rect x="98" y="20" width="16" height="34" rx="1.5" fill="#292235" />
    <rect x="99.5" y="22" width="13" height="5" rx="1" fill="#ff79c6" opacity="0.85" />
    <rect x="100.5" y="29" width="11" height="8" fill="#141420" />
    <rect x="100.5" y="39" width="11" height="4" fill="#1d1d2b" transform="skewX(-6)" />
    <circle cx="103" cy="45" r="1.1" fill="#ffd54a" />
    <circle cx="107" cy="45" r="1.1" fill="#ff5470" />
    <circle cx="18" cy="16" r="4.5" fill="#26202e" stroke="#4a4456" stroke-width="0.8" />
    <circle cx="18" cy="16" r="2.4" fill="#8a3f47" />
    <circle cx="18" cy="16" r="0.8" fill="#ffd54a" />
    {#if tier >= 2}
      <rect x="52" y="34" width="7" height="11" rx="1" fill="#1d1d29" stroke="#3a3a4c" stroke-width="0.7" />
      <circle cx="55.5" cy="41" r="2" fill="#33334a" />
      <circle cx="55.5" cy="37" r="1.1" fill="#33334a" />
    {/if}
    {#if tier >= 3}
      <rect x="62" y="20.5" width="28" height="1.4" rx="0.7" class="neonbar" fill="#ff79c6" />
    {/if}

  {:else if type === 'security'}
    <!-- lookout: blinded window + monitor desk -->
    <rect x="8" y="12" width="42" height="30" rx="1.5" fill="#0a0e18" stroke="#2a2a3c" stroke-width="1.2" />
    {#each [16, 21, 26, 31, 36] as by}
      <rect x="10" y={by} width="38" height="2.6" rx="1" fill="#232a3c" />
    {/each}
    <circle cx="41" cy="17" r="2.6" fill="#cfd8ea" opacity={night ? 0.9 : 0.25} />
    <rect x="58" y="40" width="54" height="3" rx="1.2" fill="#2c3040" />
    <rect x="62" y="43" width="4" height="10" fill="#23242f" />
    <rect x="102" y="43" width="4" height="10" fill="#23242f" />
    {#each [{ x: 62, w: 13 }, { x: 78, w: 15 }, { x: 96, w: 12 }] as mo}
      <rect x={mo.x} y="28" width={mo.w} height="10" rx="1" fill="#10141c" stroke="#3a3f4f" stroke-width="0.8" />
      <rect class="monitor" x={mo.x + 1.2} y="29.2" width={mo.w - 2.4} height="7.6" fill="#173a3c" />
      <rect x={mo.x + mo.w / 2 - 1} y="38" width="2" height="2.2" fill="#3a3f4f" />
    {/each}
    <path d="M70 24 a7 7 0 0 1 10 0" stroke="#2ed3c6" stroke-width="1.2" fill="none" opacity="0.6" />
    {#if rain}
      {#each [15, 26, 38] as rx, i}
        <line class="rainstreak" style="animation-delay:-{i * 0.7}s" x1={rx} y1="13" x2={rx - 2} y2="21" stroke="#9db4d4" stroke-width="0.7" opacity="0.5" />
      {/each}
    {/if}
    {#if tier >= 2}
      <rect x="84" y="16" width="11" height="8" rx="1" fill="#10141c" stroke="#3a3f4f" stroke-width="0.8" />
      <rect class="monitor" x="85.2" y="17.2" width="8.6" height="5.6" fill="#3c1d24" />
    {/if}
    {#if tier >= 3}
      <rect x="58" y="12" width="20" height="13" rx="1" fill="#141a24" stroke="#3a3f4f" stroke-width="0.8" />
      <path d="M60 22 l4 -5 l4 3 l5 -6 l4 4" stroke="#2ed3c6" stroke-width="0.8" fill="none" opacity="0.8" />
      <circle cx="66" cy="17" r="1" fill="#ff5470" />
      <circle cx="72" cy="15" r="1" fill="#ffd54a" />
    {/if}

  {:else}
    <!-- stash: shelving, duffels, safe, hanging bulb -->
    <rect x="8" y="16" width="44" height="38" fill="#1a1a26" />
    {#each [24, 36, 48] as sy}
      <rect x="8" y={sy} width="44" height="2" fill="#333344" />
    {/each}
    <!-- duffel bags pile up with the product stash -->
    {#each [{ x: 20, y: 20.5 }, { x: 38, y: 21 }, { x: 22, y: 32.5 }, { x: 40, y: 33 }, { x: 20, y: 44.5 }, { x: 38, y: 45 }].slice(0, 2 + duffels) as d, i}
      <ellipse cx={d.x} cy={d.y} rx={i % 2 ? 7 : 8} ry="3" fill={i % 2 ? '#4a4234' : '#3f4a3a'} />
      <rect x={d.x - 2} y={d.y - 3.4} width="4" height="1.4" rx="0.7" fill="#23232f" />
    {/each}
    <rect x="8" y="16" width="2" height="38" fill="#333344" />
    <rect x="50" y="16" width="2" height="38" fill="#333344" />
    {#if tier >= 2}
      <rect x="56" y="44" width="9" height="9" rx="1" fill="#2f5c37" stroke="#1d3a23" stroke-width="0.8" />
      <rect x="57.5" y="46" width="6" height="1.4" fill="#7ce98f" opacity="0.8" />
      <rect x="57.5" y="49" width="6" height="1.4" fill="#7ce98f" opacity="0.8" />
    {/if}
    {#if tier >= 3}
      {#each [12, 19, 26, 33, 40, 47] as bx}
        <rect x={bx} y="16" width="1.2" height="38" fill="#0b0b12" opacity="0.85" />
      {/each}
      <circle cx="83" cy="38" r="1.2" fill="#2ed3c6" />
    {/if}
    <rect x="68" y="26" width="30" height="28" rx="2" fill="#3a4048" stroke="#20242a" stroke-width="1.2" />
    <circle cx="83" cy="38" r="6" fill="#2b3036" stroke="#586068" stroke-width="1.2" />
    <circle cx="83" cy="38" r="2" fill="#586068" />
    <rect x="70.5" y="30" width="3" height="20" rx="1.2" fill="#586068" />
    <rect x="103" y="40" width="13" height="13" rx="1" fill="#5d4a30" stroke="#3a2e1d" stroke-width="0.8" />
    <line x1="103" y1="46" x2="116" y2="46" stroke="#3a2e1d" stroke-width="0.8" />
    <g class="swing">
      <line x1="60" y1="8" x2="60" y2="18" stroke="#4a4456" stroke-width="0.9" />
      <circle class="lamp" cx="60" cy="19.5" r="2" fill="#ffd9a0" />
    </g>
  {/if}
</svg>

<style>
  .scene {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  /* interior lights read stronger at night */
  .lamp {
    filter: drop-shadow(0 0 2px rgba(255, 213, 122, 0.7));
  }
  .night .lamp {
    filter: drop-shadow(0 0 4px rgba(255, 213, 122, 1));
  }
  .neon {
    font-family: 'Trebuchet MS', sans-serif;
    font-weight: 700;
    letter-spacing: 1px;
    filter: drop-shadow(0 0 2.5px rgba(255, 84, 112, 0.9));
  }
  .growlamp {
    filter: drop-shadow(0 0 3px rgba(201, 92, 255, 0.8));
  }
  .monitor,
  .tv {
    animation: flicker 3.2s steps(1) infinite;
  }
  .haze {
    opacity: 0.1;
    animation: haze 5s ease-in-out infinite;
  }
  .dollar {
    font-weight: 800;
    animation: rise 2.4s ease-out infinite;
  }
  .dollar.d2 {
    animation-delay: 1.3s;
  }
  .bubble {
    animation: rise 1.8s ease-out infinite;
  }
  .bubble.b2 {
    animation-delay: 0.9s;
  }
  .steam {
    opacity: 0;
    animation: steam 3.6s ease-out infinite;
  }
  .steam.s2 {
    animation-delay: 1.7s;
  }
  .beltglow {
    animation: belt 1.6s linear infinite;
  }
  .swing {
    transform-box: fill-box;
    transform-origin: top center;
    animation: swing 6s ease-in-out infinite;
  }
  .neonbar {
    animation: neonpulse 3.4s ease-in-out infinite;
    filter: drop-shadow(0 0 2px currentColor);
  }
  .rainstreak {
    animation: streak 1.6s linear infinite;
  }
  @keyframes neonpulse {
    0%, 100% { opacity: 0.65; }
    50% { opacity: 1; }
  }
  @keyframes streak {
    0% { opacity: 0; transform: translateY(-4px); }
    30% { opacity: 0.55; }
    100% { opacity: 0; transform: translateY(14px); }
  }
  @keyframes flicker {
    0%, 100% { opacity: 0.85; }
    12% { opacity: 1; }
    13% { opacity: 0.7; }
    38% { opacity: 0.95; }
    61% { opacity: 0.75; }
    62% { opacity: 1; }
  }
  @keyframes haze {
    0%, 100% { opacity: 0.08; transform: translateY(0); }
    50% { opacity: 0.16; transform: translateY(-2px); }
  }
  @keyframes rise {
    0% { opacity: 0; transform: translateY(0); }
    15% { opacity: 0.9; }
    100% { opacity: 0; transform: translateY(-13px); }
  }
  @keyframes steam {
    0% { opacity: 0; transform: translateY(0); }
    25% { opacity: 0.55; }
    100% { opacity: 0; transform: translateY(-6px); }
  }
  @keyframes belt {
    0% { transform: translateX(0); opacity: 0.5; }
    100% { transform: translateX(14px); opacity: 0.15; }
  }
  @keyframes swing {
    0%, 100% { transform: rotate(-4deg); }
    50% { transform: rotate(4deg); }
  }
</style>
